import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Grid, screenSizeSmaller} from 'views/components';

import JobFeed from './job_feed/JobFeed';
import JobSettings from './job_settings/JobSettings';

import backend from 'utils/backend';

const JobView = (props) => {
  const {jobRn, statusProgressWrap: progressWrap} = props;

  const [jobSettingsView, setJobSettingsView] = React.useState('PROFILE_URL');
  const [openDrawerView, setOpenDrawerView] = React.useState(false);

  const [refresh, setRefresh] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);

  const [jobData, setJob] = React.useState(null);
  const [client, setClient] = React.useState(null);
  const [applications, setApplications] = React.useState([]);
  const [externalApplications, setExternalApplications] = React.useState([]);

  const [feedTab, setFeedTab] = React.useState('INFORMATION');

  const showSettings = screenSizeSmaller('md');

  // Separate internal and outsourced applications
  const internalApplications = React.useMemo(() => {
    return applications.filter((app) => app.source !== 'EXTERNAL');
  }, [applications]);

  const outsourcedApplications = React.useMemo(() => {
    return applications.filter((app) => app.source === 'EXTERNAL');
  }, [applications]);

  // Load the data
  React.useEffect(() => {
    progressWrap(async () => {
      const jobData = await backend.career.getJob(jobRn, true, true);
      setJob(jobData.job);
      setClient(jobData.client);
      setApplications(jobData.applicants);
      setExternalApplications(jobData.externalApplicants);

      const internal = jobData.applicants?.filter((app) => app.source !== 'EXTERNAL') || [];
      const outsourced = jobData.applicants?.filter((app) => app.source === 'EXTERNAL') || [];

      if (internal.length > 0) {
        setFeedTab('INTERNAL_APPLICATIONS');
      } else if (outsourced.length > 0) {
        setFeedTab('OUTSOURCED_APPLICATIONS');
      } else if ((jobData.externalApplicants?.length || 0) > 0) {
        setFeedTab('EXTERNAL_APPLICATIONS');
      }
      setIsLoading(false);
    });
  }, [jobRn, progressWrap, refresh]);

  const jobBackend = {
    job: {
      editStatus: async (status, onSuccess) => {
        progressWrap(async () => {
          const body = {
            status,
          };
          const result = await backend.career.updateJobStatus(jobData.jobRn, body);

          setJob((prev) => ({...prev, status: result.job.status}));

          onSuccess();

          return result;
        });
      },
    },
    applications: {
      denyApplication: async (application) => {
        progressWrap(async () => {
          const result = await backend.career.updateApplicationStatus(application.jobRn, application.caregiverRn, 'REJECTED');
          setApplications((prev) => prev.map((app) =>
            app.caregiverRn === result.application.caregiverRn && app.jobRn === result.application.jobRn ? result.application : app,
          ));
          return result;
        });
      },
      acceptApplication: async (application) => {
        progressWrap(async () => {
          const result = await backend.career.updateApplicationStatus(application.jobRn, application.caregiverRn, 'ACCEPTED');
          setApplications((prev) => prev.map((app) =>
            app.caregiverRn === result.application.caregiverRn && app.jobRn === result.application.jobRn ? result.application : app,
          ));
          return result;
        });
      },
    },
    externalApplication: {
      denyApplication: async (application) => {
        progressWrap(async () => {
          const result = await backend.career.externalApplicationReject(application.jobRn, application.externalApplicationRn, 'REJECTED');
          setExternalApplications((prev) => prev.map((app) =>
            app.externalApplicationRn === result.application.externalApplicationRn ? result.application : app,
          ));
          return result;
        });
      },
      acceptApplication: async (application) => {
        progressWrap(async () => {
          const result = await backend.career.externalApplicationAccept(application.jobRn, application.externalApplicationRn, 'ACCEPTED');
          setExternalApplications((prev) => prev.map((app) =>
            app.externalApplicationRn === result.application.externalApplicationRn ? result.application : app,
          ));
          return result;
        });
      },
    },
    outsourcedApplication: {
      denyApplication: async (application) => {
        progressWrap(async () => {
          const result = await backend.career.outsourcedApplicationReject(application.jobRn, application.caregiverRn);
          setApplications((prev) => prev.map((app) =>
            app.caregiverRn === result.application.caregiverRn && app.jobRn === result.application.jobRn ? result.application : app,
          ));
          return result;
        });
      },
      acceptApplication: async (application) => {
        progressWrap(async () => {
          const result = await backend.career.outsourcedApplicationAccept(application.jobRn, application.caregiverRn);
          setApplications((prev) => prev.map((app) =>
            app.caregiverRn === result.application.caregiverRn && app.jobRn === result.application.jobRn ? result.application : app,
          ));
          return result;
        });
      },
    },
    interview: {
      getInterview: async (caregiverRn, interviewRn) => {
        return backend.caregiver.interview.get(caregiverRn, interviewRn);
      },
    },
  };

  if (isLoading) return null;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={9}>
        <JobFeed
          jobData={jobData}
          applications={internalApplications}
          outsourcedApplications={outsourcedApplications}
          externalApplications={externalApplications}
          client={client}
          jobBackend={jobBackend}
          feedTab={feedTab}
          setFeedTab={setFeedTab}
          refresh={refresh}
          setRefresh={setRefresh}
          showSettings={showSettings}
          setOpenDrawerView={setOpenDrawerView}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <JobSettings
          jobData={jobData}
          showSettings={showSettings}
          openDrawerView={openDrawerView}
          setOpenDrawerView={setOpenDrawerView}
          jobSettingsView={jobSettingsView}
          setJobSettingsView={setJobSettingsView}
          refresh={refresh}
          setRefresh={setRefresh}
        />
      </Grid>
    </Grid>
  );
};

JobView.propTypes = {
  jobRn: PropTypes.string.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  requester: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(JobView));
